/* AI Studio — 3-step wizard: 1) Crea il capo · 2) Visualizza · 3) Tech pack.
 *
 * Reuses the existing backend endpoints:
 *   POST /ai/technical-drawing   — flat views (front/right/left/back)
 *   POST /ai/mockup              — mockups (studio/runway/editorial/ecommerce/custom)
 *   POST /ai/techpack-draft      — tech pack JSON draft
 *   PATCH /garments/{gid}/techpack-draft
 *
 * The wizard state is preserved when the user moves between steps and
 * auto-persisted to localStorage so a page refresh doesn't lose progress.
 */
import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/ui-bits";
import { AiAssistantDock, AskAiButton } from "../components/AiAssistant";
import {
  Sparkles, Upload, Trash2, Download, Image as ImageIcon, FileText, Loader2,
  Save, Check, ChevronRight, ChevronLeft, RefreshCw, ChevronDown, ChevronUp,
  BookmarkPlus, FileDown, Lock, Unlock, FolderPlus, X,
} from "lucide-react";

const CATEGORIES = ["Jackets","Coats","Shirts","T-shirts","Knitwear","Pants","Shorts","Skirts","Dresses","Accessories","Dust bags","Packaging"];
const SEASONS = ["", "FW25", "SS26", "FW26", "SS27", "FW27", "SS28"];
const GENDERS = ["", "male", "female", "unisex"];
const BODY_TYPES = ["", "athletic", "slim", "curvy", "plus", "average"];

const VIEW_ORDER = ["front", "right", "left", "back"];
const VIEW_LABELS = { front: "Fronte", right: "Lato destro", left: "Lato sinistro", back: "Retro" };

const STYLES = [
  { key: "technical", label: "Technical", hint: "flat sketch B/N · tech pack" },
  { key: "realistic", label: "Realistic", hint: "foto reale · ghost mannequin" },
  { key: "hybrid",    label: "Hybrid",    hint: "tecnico + resa materica" },
];

const VIS_MODES = [
  { key: "lookbook",  label: "Lookbook",  hint: "capo indossato · set completo" },
  { key: "dettagli",  label: "Dettagli",  hint: "close-up · zip · cuciture ..." },
  { key: "campagna",  label: "Campagna",  hint: "editorial · advertising" },
  { key: "no_model",  label: "No model",  hint: "flat lay · ghost · still life" },
];
const LOOKBOOK_SHOTS  = ["front", "three_quarter", "back", "side", "walking", "detail"];
const DETAILS_SHOTS   = ["zip", "buttons", "seams", "pockets", "embroidery", "fabric", "finishing", "interior", "hood", "cuffs", "collar", "accessories"];
const CAMPAIGN_SHOTS  = ["editorial", "advertising", "walking", "close_up", "full_body", "static", "runway_mood", "social"];
const NO_MODEL_SHOTS  = ["flat_lay", "ghost_mannequin", "hanging", "floating", "still_life"];
const SHOTS_FOR_MODE = {
  lookbook: LOOKBOOK_SHOTS,
  dettagli: DETAILS_SHOTS,
  campagna: CAMPAIGN_SHOTS,
  no_model: NO_MODEL_SHOTS,
};
// The minimum default selection so each mode produces a proper "set"
const DEFAULT_SHOTS = {
  lookbook: LOOKBOOK_SHOTS,                 // all 6
  dettagli: ["zip", "buttons", "seams", "pockets"],
  campagna: ["editorial", "walking", "close_up", "full_body", "advertising", "static"], // 6
  no_model: NO_MODEL_SHOTS,
};
// Legacy — kept only for older subs listing (single-shot fallback)
const LOOKBOOK_SUBS  = LOOKBOOK_SHOTS;
const CAMPAIGN_SUBS  = CAMPAIGN_SHOTS;
const ON_MODEL_SUBS  = ["neutra", "camminata", "seduto", "full_body", "close_up", "custom"];
const NO_MODEL_SUBS  = NO_MODEL_SHOTS;
const LOCATION_PRESETS = ["studio_bianco", "studio_grigio", "studio_scuro", "outdoor_urbano", "indoor_luxury", "industrial", "nature", "runway", "custom"];
const MOOD_OPTIONS = ["minimal", "editorial", "cinematic", "playful", "moody"];
const ASPECT_RATIOS = ["1:1", "4:5", "3:4", "2:3", "3:2", "16:9"];
const TIERS = [
  { key: "draft",    label: "Draft",    hint: "test veloci · economico" },
  { key: "standard", label: "Standard", hint: "default · qualità/prezzo" },
  { key: "pro",      label: "Pro",      hint: "campagne · e-com premium" },
];

const LS_KEY = "aiStudio.wizard.v1";

// -------------------- Storage helpers --------------------
const persist = (state) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
};
const restore = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
};

export default function AiPage() {
  const [step, setStep] = useState(1);
  // ---- STEP 1 state ----
  const [garments, setGarments] = useState([]);
  const [garmentId, setGarmentId] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Jackets");
  const [season, setSeason] = useState("");
  const [gender, setGender] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [construction, setConstruction] = useState("");
  const [materials, setMaterials] = useState("");
  const [refs, setRefs] = useState([]); // {file, preview}
  const [drawings, setDrawings] = useState(null); // /ai/technical-drawing result
  const [style, setStyle] = useState("technical");
  const [lockedViews, setLockedViews] = useState([]);     // view names (kept as arrays for JSON persistence)
  const [approvedViews, setApprovedViews] = useState([]);

  // ---- STEP 2 state ----
  const [mode, setMode] = useState("lookbook");
  const [sub, setSub] = useState("front");
  const [selectedShots, setSelectedShots] = useState(DEFAULT_SHOTS["lookbook"]);
  const [withModel, setWithModel] = useState(false); // Dettagli mode
  const [location, setLocation] = useState("studio_bianco");
  const [mood, setMood] = useState("minimal");
  const [bodyType, setBodyType] = useState("");
  const [visNotes, setVisNotes] = useState("");
  const [variations, setVariations] = useState(2);
  const [aspectRatio, setAspectRatio] = useState("3:4");
  const [tier, setTier] = useState("standard");
  const [mockups, setMockups] = useState([]); // array of {id, mode, files:[fileDocs]}
  const [approvedRefs, setApprovedRefs] = useState([]); // list of file ids
  const [selectedMockFiles, setSelectedMockFiles] = useState([]); // multi-selection across all sets
  const [folders, setFolders] = useState([]);          // for the "save to folder" picker
  const [folderPicker, setFolderPicker] = useState(null); // { fileIds: string[] } or null
  useEffect(() => {
    api.get("/folders").then(r => setFolders(r.data || [])).catch(() => {});
  }, []);
  // Reset the shot selection whenever the mode changes.
  useEffect(() => {
    setSelectedShots(DEFAULT_SHOTS[mode] || []);
  }, [mode]);

  // ---- STEP 3 state ----
  const [techpack, setTechpack] = useState(null);
  const [tpOpen, setTpOpen] = useState({ general: true, views: true, refs: true, materials: true, trims: true, graphics: false, construction: true, measurements: false, supplier: false });

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");

  // Restore on mount
  useEffect(() => {
    const s = restore();
    if (s) {
      if (s.step) setStep(s.step);
      if (s.name !== undefined) setName(s.name);
      if (s.category) setCategory(s.category);
      if (s.season !== undefined) setSeason(s.season);
      if (s.gender !== undefined) setGender(s.gender);
      if (s.description) setDescription(s.description);
      if (s.keywords) setKeywords(s.keywords);
      if (s.construction) setConstruction(s.construction);
      if (s.materials) setMaterials(s.materials);
      if (s.garmentId) setGarmentId(s.garmentId);
      if (s.drawings) setDrawings(s.drawings);
      if (s.style) setStyle(s.style);
      if (s.lockedViews) setLockedViews(s.lockedViews);
      if (s.approvedViews) setApprovedViews(s.approvedViews);
      if (s.mockups) setMockups(s.mockups);
      if (s.approvedRefs) setApprovedRefs(s.approvedRefs);
      if (s.techpack) setTechpack(s.techpack);
    }
    api.get("/garments").then(r => setGarments(r.data)).catch(() => {});
  }, []);

  // Auto-save (skip files/refs which are transient)
  useEffect(() => {
    persist({ step, garmentId, name, category, season, gender, description, keywords, construction, materials, drawings, style, lockedViews, approvedViews, mockups, approvedRefs, techpack });
  }, [step, garmentId, name, category, season, gender, description, keywords, construction, materials, drawings, style, lockedViews, approvedViews, mockups, approvedRefs, techpack]);

  // Pull a selected garment's basic fields into the form
  useEffect(() => {
    if (!garmentId) return;
    const g = garments.find(x => x.id === garmentId);
    if (!g) return;
    setName(g.name || name);
    if (g.category) setCategory(g.category);
    if (g.season) setSeason(g.season || "");
    if (g.gender) setGender(g.gender || "");
    if (g.description) setDescription(g.description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garmentId, garments]);

  const flashMsg = (m) => { setFlash(m); setTimeout(()=>setFlash(""), 2500); };
  const canGoStep2 = drawings && drawings.files?.length > 0;
  const canGoStep3 = canGoStep2;

  // ---------- STEP 1 ----------
  const addRefFiles = (list) => {
    const next = [...refs];
    for (const f of list || []) { if (next.length >= 4) break; next.push({ file: f, preview: URL.createObjectURL(f) }); }
    setRefs(next);
  };
  const generateDrawings = async (opts = {}) => {
    // opts: { onlyViews?: string[] } — regenerate a subset (e.g. only unlocked)
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      for (const r of refs) fd.append("files", r.file);
      fd.append("category", category);
      const notes = [description, keywords ? `Keywords: ${keywords}` : "", construction ? `Construction: ${construction}` : "", materials ? `Materials: ${materials}` : ""].filter(Boolean).join("\n");
      if (notes) fd.append("notes", notes);
      if (garmentId) fd.append("garment_id", garmentId);
      fd.append("tier", tier);
      fd.append("style", style);
      // Skip locked views unless the caller explicitly asks for them.
      const desired = (opts.onlyViews && opts.onlyViews.length ? opts.onlyViews : VIEW_ORDER).filter(v => !lockedViews.includes(v));
      if (desired.length === 0) {
        setErr("Tutte le viste sono bloccate — sblocca almeno una vista prima di rigenerare.");
        return;
      }
      if (desired.length !== VIEW_ORDER.length) fd.append("views", desired.join(","));
      const r = await api.post("/ai/technical-drawing", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
      // Merge into existing drawings — keep locked/previous files for views we didn't ask to regenerate.
      setDrawings(prev => {
        const prevFiles = (prev?.files || []).filter(f => !desired.includes(f.view));
        const prevStatus = (prev?.status || []).filter(s => !desired.includes(s.view));
        return {
          files: [...prevFiles, ...(r.data.files || [])],
          status: [...prevStatus, ...(r.data.status || [])],
          tier: r.data.tier || tier,
          style: r.data.style || style,
        };
      });
      flashMsg(desired.length === VIEW_ORDER.length ? "Viste generate — controlla e approva." : `Rigenerate: ${desired.join(", ")}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const regenerateView = async (view) => {
    if (!drawings?.files?.length) return;
    if (lockedViews.includes(view)) { flashMsg(`Vista ${VIEW_LABELS[view]} bloccata — sblocca prima di rigenerare.`); return; }
    setErr(""); setBusy(true);
    try {
      const master = drawings.files.find(f => f.view === "front");
      const others = drawings.files.filter(f => f.view !== view).map(f => f.id);
      const r = await api.post("/ai/regenerate-view", {
        view, category,
        notes: [description, keywords, construction, materials].filter(Boolean).join("\n"),
        garment_id: garmentId || null,
        master_file_id: master?.id || null,
        ref_file_ids: others,
        tier, style,
      });
      setDrawings(prev => {
        if (!prev) return prev;
        const next = { ...prev };
        next.files = (prev.files || []).filter(f => f.view !== view).concat([r.data.file]);
        next.status = (prev.status || []).map(s => s.view === view ? { view, status: "ok", file_id: r.data.file.id } : s);
        if (!next.status.find(s => s.view === view)) next.status.push({ view, status: "ok", file_id: r.data.file.id });
        return next;
      });
      // Regenerating invalidates the previous approval on that view.
      setApprovedViews(prev => prev.filter(v => v !== view));
      flashMsg(`Vista ${VIEW_LABELS[view]} rigenerata.`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const toggleLock = (view) => setLockedViews(prev => prev.includes(view) ? prev.filter(v => v !== view) : [...prev, view]);
  const toggleApproveView = (view) => setApprovedViews(prev => prev.includes(view) ? prev.filter(v => v !== view) : [...prev, view]);
  const approveAndContinue = () => { setStep(2); flashMsg("Capo approvato. Passa alla visualizzazione."); };

  // ---------- STEP 2 ----------
  const generateVisuals = async () => {
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      if (garmentId) fd.append("garment_id", garmentId);
      // SOURCE OF TRUTH: the Step-1 approved views (or all Step-1 views if none is explicitly
      // approved). These lock the garment identity end-to-end.
      const step1Files = drawings?.files || [];
      const approvedIds = step1Files.filter(f => approvedViews.includes(f.view)).map(f => f.id);
      const step1RefIds = approvedIds.length ? approvedIds : step1Files.map(f => f.id);
      if (step1RefIds.length) fd.append("ref_file_ids", step1RefIds.join(","));
      const legacy = mode === "no_model" ? "custom" : "studio";
      fd.append("mockup_type", legacy);
      fd.append("mode", mode);
      // shots CSV — one image per selected shot
      const shots = (selectedShots && selectedShots.length ? selectedShots : DEFAULT_SHOTS[mode] || []).slice(0, 12);
      if (shots.length) fd.append("shots", shots.join(","));
      if (mode === "dettagli") fd.append("with_model", withModel ? "true" : "false");
      fd.append("location", location);
      fd.append("mood", mood);
      fd.append("aspect_ratio", aspectRatio);
      fd.append("tier", tier);
      if (gender) fd.append("gender", gender);
      if (bodyType) fd.append("body_type", bodyType);
      if (visNotes) fd.append("notes", visNotes);
      const r = await api.post("/ai/mockup", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 360000 });
      const entry = { id: Date.now(), mode, shots, location, tier, files: r.data.files || [], status: r.data.status || [] };
      setMockups(prev => [entry, ...prev]);
      flashMsg(`Set generato · ${(r.data.files || []).length} immagini`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const toggleApproved = (fid) => setApprovedRefs(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);
  const toggleSelectMockFile = (fid) => setSelectedMockFiles(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);
  const approveManyMockFiles = (ids) => setApprovedRefs(prev => Array.from(new Set([...prev, ...ids])));
  const saveFilesToFolder = async (fileIds, folder) => {
    if (!fileIds?.length) return;
    setBusy(true); setErr("");
    try {
      const payload = { file_ids: fileIds };
      if (folder?.id) payload.folder_id = folder.id;
      else if (folder?.name) payload.folder_name = folder.name;
      const r = await api.post("/ai/save-files-to-folder", payload);
      // refresh folder list (a new one may have been created)
      api.get("/folders").then(res => setFolders(res.data || [])).catch(() => {});
      flashMsg(`Salvate ${r.data.moved} immagini in cartella`);
      setFolderPicker(null);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---------- STEP 3 ----------
  const generateTechpack = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        garment_id: garmentId || null,
        garment_name: name || null,
        category, gender: gender || null, season: season || null,
        description: [description, keywords ? `Keywords: ${keywords}` : "", construction, materials].filter(Boolean).join("\n") || null,
        fabric: materials || null,
        save_to_garment: !!garmentId,
      };
      const r = await api.post("/ai/techpack-draft", payload);
      const draft = r.data.draft || r.data;
      // Build nested shape (matches /ai/techpack/pdf schema)
      setTechpack({
        ...draft,   // keep flat fields for backwards compat
        general: {
          name: name || draft.garment_name || "",
          code: draft.article_code || (garments.find(g => g.id === garmentId)?.code) || "",
          collection: draft.collection || "",
          category, gender: gender || "", season: season || "",
          description: description || draft.description || "",
        },
        materials: {
          main_fabric: materials || draft.fabric || "",
          secondary_fabrics: "",
          composition: draft.composition || "",
          weight: draft.gsm || "",
          color: draft.colorways || "",
          supplier: "", supplier_code: "",
          quantity: "", notes: "",
        },
        trims: {
          buttons: draft.buttons || "",
          zippers: draft.zippers || "",
          snaps: "",
          drawcords: "",
          elastic: "",
          labels: draft.labels || "",
          hangtags: "",
          packaging: draft.packaging_notes || "",
          accessories: draft.accessories || "",
        },
        graphics: {
          prints: "", embroidery: "", applications: "", washing: "",
          treatments: "", special: draft.special_finishing || "",
        },
        construction: {
          seams: "",
          stitching: draft.stitching_details || "",
          pockets: "",
          closures: draft.closures || "",
          finishing: "",
          internal: "",
          assembly: draft.construction_notes || "",
        },
        measurements: {
          sample_size: draft.sample_size || "",
          points: "", grading: draft.size_range || "",
        },
        supplier_notes: draft.production_notes || draft.seamstress_notes || "",
        views: (drawings?.files || []).map(f => ({ file_id: f.id, view: f.view, filename: f.original_filename })),
        approved_refs: approvedRefs.slice(),
      });
      flashMsg("Bozza tech pack generata.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const saveTechpack = async () => {
    if (!techpack) return;
    setErr(""); setBusy(true);
    try {
      if (garmentId) {
        await api.patch(`/garments/${garmentId}/techpack-draft`, { techpack_draft: techpack });
        flashMsg("Tech pack salvato sul capo.");
      } else {
        flashMsg("Tech pack salvato localmente. Collega un capo per persistere sul database.");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const saveToCollection = async () => {
    setErr(""); setBusy(true);
    try {
      const view_file_ids = (drawings?.files || []).map(f => f.id);
      const reference_file_ids = approvedRefs.slice();
      const payload = {
        garment_id: garmentId || null,
        name: name || techpack?.general?.name || "AI Studio garment",
        code: techpack?.general?.code || null,
        category, gender: gender || null, season: season || null,
        description: description || null,
        view_file_ids, reference_file_ids,
        techpack: techpack || null,
      };
      const r = await api.post("/ai/save-to-collection", payload);
      setGarmentId(r.data.id);
      // refresh garments list
      api.get("/garments").then(res => setGarments(res.data)).catch(() => {});
      flashMsg(garmentId ? "Capo aggiornato in Collezioni & Capi." : "Capo creato in Collezioni & Capi.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const exportTechpackPdf = async () => {
    if (!techpack) return;
    setErr(""); setBusy(true);
    try {
      const view_file_ids = (drawings?.files || []).map(f => f.id);
      const reference_file_ids = approvedRefs.slice();
      const r = await api.post("/ai/techpack/pdf", {
        techpack, view_file_ids, reference_file_ids,
      }, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `techpack-${(techpack?.general?.code || "draft")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      flashMsg("Tech Pack PDF pronto.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader
        eyebrow="Intelligence"
        title="AI Studio"
        description="Un flusso guidato: idea → viste base → mockup → tech pack fornitore."
      />

      {/* Stepper */}
      <div className="border border-[#E5E5E5] bg-white flex mb-6" data-testid="ai-stepper">
        {[
          { n: 1, label: "Crea il capo", enabled: true },
          { n: 2, label: "Visualizza",   enabled: canGoStep2 },
          { n: 3, label: "Tech pack",    enabled: canGoStep3 },
        ].map((s, i) => (
          <button
            key={s.n}
            onClick={() => s.enabled && setStep(s.n)}
            disabled={!s.enabled}
            data-testid={`ai-step-btn-${s.n}`}
            className={`flex-1 flex items-center gap-3 px-5 py-4 text-left border-r border-[#E5E5E5] last:border-r-0 transition-colors ${step === s.n ? "bg-[#0A0A0A] text-white" : s.enabled ? "bg-white text-[#0A0A0A] hover:bg-[#FAFAFA]" : "bg-[#FAFAFA] text-[#a3a3a3] cursor-not-allowed"}`}
          >
            <div className={`w-6 h-6 border flex items-center justify-center text-xs ${step === s.n ? "border-white" : "border-[#0A0A0A]"}`}>
              {step > s.n ? <Check size={12}/> : s.n}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Step {s.n}</div>
              <div className="font-serif text-lg">{s.label}</div>
            </div>
            {i < 2 && <ChevronRight size={14} className="ml-auto opacity-50"/>}
          </button>
        ))}
      </div>

      {flash && <div className="mb-4 p-3 border border-[#0A0A0A] bg-[#FAFAFA] text-xs text-[#0A0A0A]" data-testid="ai-flash">{flash}</div>}
      {err && <div className="mb-4 p-3 border border-red-300 bg-red-50 text-xs text-red-700" data-testid="ai-error">{err}</div>}

      {step === 1 && (
        <Step1
          garments={garments} garmentId={garmentId} setGarmentId={setGarmentId}
          name={name} setName={setName}
          category={category} setCategory={setCategory}
          season={season} setSeason={setSeason}
          gender={gender} setGender={setGender}
          description={description} setDescription={setDescription}
          keywords={keywords} setKeywords={setKeywords}
          construction={construction} setConstruction={setConstruction}
          materials={materials} setMaterials={setMaterials}
          refs={refs} setRefs={setRefs} addRefFiles={addRefFiles}
          drawings={drawings} busy={busy}
          tier={tier} setTier={setTier}
          style={style} setStyle={setStyle}
          lockedViews={lockedViews} approvedViews={approvedViews}
          onToggleLock={toggleLock} onToggleApproveView={toggleApproveView}
          onGenerate={generateDrawings} onApprove={approveAndContinue}
          onRegenerateView={regenerateView}
          canGoNext={canGoStep2}
        />
      )}

      {step === 2 && (
        <Step2
          mode={mode} setMode={setMode}
          sub={sub} setSub={setSub}
          selectedShots={selectedShots} setSelectedShots={setSelectedShots}
          withModel={withModel} setWithModel={setWithModel}
          location={location} setLocation={setLocation}
          mood={mood} setMood={setMood}
          bodyType={bodyType} setBodyType={setBodyType}
          gender={gender} setGender={setGender}
          visNotes={visNotes} setVisNotes={setVisNotes}
          variations={variations} setVariations={setVariations}
          aspectRatio={aspectRatio} setAspectRatio={setAspectRatio}
          tier={tier} setTier={setTier}
          mockups={mockups} setMockups={setMockups}
          approvedRefs={approvedRefs} toggleApproved={toggleApproved}
          selectedMockFiles={selectedMockFiles} toggleSelectMockFile={toggleSelectMockFile}
          approveManyMockFiles={approveManyMockFiles}
          folders={folders}
          onSaveFilesToFolder={saveFilesToFolder}
          folderPicker={folderPicker} setFolderPicker={setFolderPicker}
          drawings={drawings} approvedViews={approvedViews}
          busy={busy} onGenerate={generateVisuals}
          onBack={() => setStep(1)} onNext={() => setStep(3)}
        />
      )}

      {step === 3 && (
        <Step3
          techpack={techpack} setTechpack={setTechpack}
          drawings={drawings} mockups={mockups} approvedRefs={approvedRefs}
          garments={garments} garmentId={garmentId} setGarmentId={setGarmentId}
          busy={busy} onGenerate={generateTechpack} onSave={saveTechpack}
          onSaveToCollection={saveToCollection} onExportPdf={exportTechpackPdf}
          tpOpen={tpOpen} setTpOpen={setTpOpen}
          onBack={() => setStep(2)}
        />
      )}

      <AiAssistantDock
        kind={step === 3 ? "techpack" : step === 1 ? "description" : "generic"}
        context={JSON.stringify({ step, name, category, season, gender, keywords }).slice(0, 800)}
        suggestions={
          step === 1
            ? [
                "Aiutami a scrivere una descrizione professionale del capo",
                "Suggerisci 8 keywords rilevanti per questa collezione",
                "Elenca materiali coerenti con questo capo",
              ]
            : step === 2
              ? [
                  "Suggerisci mood coerente con la stagione",
                  "Consigli per una campaign editoriale",
                  "Location perfetta per e-commerce premium",
                ]
              : [
                  "Consigli di costruzione per un capo di questa categoria",
                  "Suggerisci una tabella taglie standard S-M-L",
                  "Note per il fornitore su cuciture e finiture",
                ]
        }
      />
    </div>
  );
}

// ==================== STEP 1 ====================
function Step1(props) {
  const {
    garments, garmentId, setGarmentId,
    name, setName, category, setCategory, season, setSeason, gender, setGender,
    description, setDescription, keywords, setKeywords,
    construction, setConstruction, materials, setMaterials,
    refs, setRefs, addRefFiles, drawings, busy,
    tier, setTier, style, setStyle,
    lockedViews = [], approvedViews = [],
    onToggleLock, onToggleApproveView,
    onGenerate, onApprove, onRegenerateView, canGoNext,
  } = props;
  const fileRef = useRef();
  const hasDrawings = !!drawings?.files?.length;
  const unlockedCount = VIEW_ORDER.filter(v => !lockedViews.includes(v)).length;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="ai-step-1">
      {/* LEFT — INPUT */}
      <div className="card-dark p-6 space-y-4 lg:col-span-2">
        <div className="flex items-center justify-between">
          <div className="font-serif text-2xl">Descrivi il capo</div>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm("Reset della bozza AI Studio? Tornerai a un capo vuoto.")) return;
              try { localStorage.removeItem("ai-studio-draft-v1"); } catch {}
              window.location.reload();
            }}
            title="Cancella lo stato locale e ricomincia"
            className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A]"
            data-testid="s1-reset-draft"
          >
            Reset bozza
          </button>
        </div>

        <FormField label="Nome capo">
          <input className="input-dark" value={name} onChange={(e)=>setName(e.target.value)} placeholder="es. Denim Jacket Oversize" data-testid="s1-name"/>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Categoria">
            <select className="input-dark" value={category} onChange={(e)=>setCategory(e.target.value)} data-testid="s1-category">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </FormField>
          <FormField label="Stagione">
            <select className="input-dark" value={season} onChange={(e)=>setSeason(e.target.value)} data-testid="s1-season">
              {SEASONS.map(s => <option key={s || "none"} value={s}>{s || "—"}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Gender / target">
          <select className="input-dark" value={gender} onChange={(e)=>setGender(e.target.value)} data-testid="s1-gender">
            {GENDERS.map(g => <option key={g || "none"} value={g}>{g || "—"}</option>)}
          </select>
        </FormField>

        <FormField label="Descrizione testuale">
          <textarea rows={3} className="input-dark" value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Silhouette, dettagli, ispirazione..." data-testid="s1-description"/>
          <div className="mt-1 flex justify-end">
            <AskAiButton
              kind="description"
              label="Scrivi per me"
              prompt={`Scrivi una descrizione professionale (max 60 parole) per un capo di categoria ${category}${season ? ", stagione " + season : ""}${gender ? ", target " + gender : ""}${keywords ? ". Keywords: " + keywords : ""}${name ? ". Nome capo: " + name : ""}. Rispondi in italiano.`}
              onResult={(t) => setDescription(t)}
            />
          </div>
        </FormField>

        <FormField label="Keywords">
          <input className="input-dark" value={keywords} onChange={(e)=>setKeywords(e.target.value)} placeholder="oversize, workwear, raw denim, contrast stitching..." data-testid="s1-keywords"/>
          <div className="mt-1 flex justify-end">
            <AskAiButton
              kind="generic"
              label="Suggerisci"
              prompt={`Suggerisci 8 keywords (separate da virgola) per un capo ${category}${season ? " " + season : ""}${description ? " descritto come: " + description : ""}. Solo la lista, nessuna spiegazione.`}
              onResult={(t) => setKeywords(t.replace(/^[\s\-•]+/, "").trim())}
            />
          </div>
        </FormField>

        <FormField label="Note stilistiche / costruttive">
          <textarea rows={2} className="input-dark" value={construction} onChange={(e)=>setConstruction(e.target.value)} placeholder="Cuciture flat-felled, tasche a pattina, bottoni metallici antichizzati..."/>
        </FormField>

        <FormField label="Materiali base">
          <input className="input-dark" value={materials} onChange={(e)=>setMaterials(e.target.value)} placeholder="14 oz raw denim, fodera in cotone..."/>
        </FormField>

        <FormField label="Immagini reference (max 4)">
          <div className="flex flex-wrap gap-3">
            {refs.map((r, i) => (
              <div key={i} className="relative">
                <img src={r.preview} alt="ref" className="w-24 h-28 object-cover border border-[#E5E5E5]"/>
                <button type="button" onClick={()=>setRefs(refs.filter((_,j)=>j!==i))} className="absolute top-1 right-1 bg-black/70 p-1 text-red-500"><Trash2 size={10}/></button>
              </div>
            ))}
            {refs.length < 4 && (
              <button type="button" onClick={()=>fileRef.current?.click()} className="dropzone w-24 h-28 flex items-center justify-center text-[#737373]" data-testid="s1-upload">
                <Upload size={16}/>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e)=>{ addRefFiles(e.target.files); e.target.value=""; }}/>
          </div>
        </FormField>

        <FormField label="Oppure: seleziona un capo esistente (opzionale)">
          <select className="input-dark" value={garmentId} onChange={(e)=>setGarmentId(e.target.value)} data-testid="s1-garment">
            <option value="">— Nessuno —</option>
            {garments.map(g => <option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}
          </select>
        </FormField>

        <TierPicker tier={tier} setTier={setTier} testid="s1-tier"/>
        <StylePicker style={style} setStyle={setStyle} testid="s1-style"/>

        {!hasDrawings ? (
          <button
            disabled={busy || (refs.length === 0 && !garmentId && !((description || "").trim() || (keywords || "").trim() || (construction || "").trim() || (materials || "").trim()))}
            onClick={() => onGenerate()}
            className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="s1-generate"
            title={refs.length === 0 && !garmentId ? "Generazione text-to-image: compila almeno descrizione / keywords / note / materiali" : ""}
          >
            {busy ? <><Loader2 size={14} className="animate-spin"/> Genero le 4 viste (fino a 120s)…</> : <><Sparkles size={14}/> Genera fronte · destro · sinistro · retro</>}
          </button>
        ) : (
          <div className="space-y-2">
            <button
              disabled={busy || unlockedCount === 0}
              onClick={() => onGenerate()}
              className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="s1-apply-changes"
              title="Rigenera le viste non bloccate usando i campi aggiornati"
            >
              {busy ? <><Loader2 size={14} className="animate-spin"/> Applico le modifiche…</> : <><RefreshCw size={14}/> Applica modifiche · Rigenera {unlockedCount}/4</>}
            </button>
            <div className="text-[10px] text-[#737373] uppercase tracking-widest">
              Modifica i campi qui sopra o blocca 🔒 le viste da preservare, poi applica.
            </div>
          </div>
        )}
        <div className="text-[10px] text-[#737373] uppercase tracking-widest">
          Output: 4 viste coerenti — {STYLES.find(s => s.key === style)?.label || style}.
        </div>
      </div>

      {/* RIGHT — OUTPUT 2x2 grid */}
      <div className="lg:col-span-3">
        {!drawings && !busy && (
          <div className="card-dark p-10 text-center text-[#737373]" data-testid="s1-empty">
            <ImageIcon size={40} className="mx-auto text-[#D4D4D4] mb-4"/>
            <div className="font-serif text-xl text-[#0A0A0A] mb-2">Fronte · Destro · Sinistro · Retro</div>
            <div className="text-xs uppercase tracking-widest">Carica reference o seleziona un capo, poi genera.</div>
          </div>
        )}
        {busy && !drawings && (
          <div className="card-dark p-10 flex flex-col items-center justify-center text-[#737373]" data-testid="s1-loading">
            <Loader2 size={28} className="animate-spin text-[#0A0A0A] mb-4"/>
            <div className="text-xs uppercase tracking-widest">Generazione in corso — fronte, destro, sinistro, retro</div>
          </div>
        )}
        {drawings && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4" data-testid="s1-results">
              {VIEW_ORDER.map(view => {
                const st = drawings.status?.find(s => s.view === view);
                const file = st?.status === "ok" ? drawings.files.find(f => f.id === st.file_id) : null;
                return (
                  <div key={view} className="card-dark overflow-hidden">
                    <div className="aspect-square bg-white flex items-center justify-center">
                      {file ? (
                        <img
                          src={fileUrl(file.id)} alt={view}
                          className="w-full h-full object-contain"
                          data-testid={`s1-img-${view}`}
                          onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                        />
                      ) : st?.status === "error" ? (
                        <div className="text-red-600 text-xs uppercase tracking-widest text-center px-4">
                          Generazione fallita — riprova
                        </div>
                      ) : st?.status === "no_image" ? (
                        <div className="text-[#737373] text-xs uppercase tracking-widest text-center px-4">
                          Nessun output — tocca ↻ per rigenerare
                        </div>
                      ) : busy ? (
                        <div className="text-[#737373] text-xs uppercase tracking-widest text-center px-4 inline-flex flex-col items-center gap-2">
                          <Loader2 size={18} className="animate-spin text-[#0A0A0A]"/> Generating…
                        </div>
                      ) : (
                        <div className="text-[#737373] text-xs uppercase tracking-widest text-center px-4">In attesa</div>
                      )}
                    </div>
                    <div className="p-3 flex items-center justify-between border-t border-[#E5E5E5]">
                      <div className="flex items-center gap-2">
                        <div className="label-caps text-[#0A0A0A]">{VIEW_LABELS[view]}</div>
                        {approvedViews.includes(view) && <span className="text-[10px] uppercase tracking-widest text-white bg-[#0A0A0A] px-1.5 py-0.5">Approvata</span>}
                        {lockedViews.includes(view) && <span className="text-[10px] uppercase tracking-widest border border-[#0A0A0A] text-[#0A0A0A] px-1.5 py-0.5">Bloccata</span>}
                      </div>
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={()=>onToggleApproveView?.(view)}
                          disabled={!file}
                          title={approvedViews.includes(view) ? "Rimuovi approvazione" : "Approva questa vista"}
                          className={`hover:text-[#0A0A0A] disabled:opacity-40 ${approvedViews.includes(view) ? "text-[#0A0A0A]" : "text-[#525252]"}`}
                          data-testid={`s1-approve-${view}`}
                        >
                          <Check size={12}/>
                        </button>
                        <button
                          onClick={()=>onToggleLock?.(view)}
                          disabled={!file}
                          title={lockedViews.includes(view) ? "Sblocca vista" : "Blocca vista"}
                          className={`hover:text-[#0A0A0A] disabled:opacity-40 ${lockedViews.includes(view) ? "text-[#0A0A0A]" : "text-[#525252]"}`}
                          data-testid={`s1-lock-${view}`}
                        >
                          {lockedViews.includes(view) ? <Lock size={12}/> : <Unlock size={12}/>}
                        </button>
                        <button
                          onClick={()=>onRegenerateView?.(view)}
                          disabled={busy || lockedViews.includes(view)}
                          title={lockedViews.includes(view) ? "Vista bloccata — sblocca prima" : "Rigenera solo questa vista"}
                          className="text-[#525252] hover:text-[#0A0A0A] disabled:opacity-40"
                          data-testid={`s1-regen-${view}`}
                        >
                          <RefreshCw size={12}/>
                        </button>
                        {file && <a href={fileUrl(file.id)} download={file.original_filename} className="text-[#525252] hover:text-[#0A0A0A]"><Download size={13}/></a>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={onGenerate} disabled={busy} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="s1-regenerate">
                <RefreshCw size={12}/> Rigenera
              </button>
              <button onClick={onApprove} disabled={!canGoNext} className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-40" data-testid="s1-approve">
                Approva e visualizza <ChevronRight size={12}/>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== STEP 2 ====================
function Step2(props) {
  const {
    mode, setMode, sub, setSub, location, setLocation, mood, setMood,
    bodyType, setBodyType, gender, setGender, visNotes, setVisNotes,
    variations, setVariations, mockups, approvedRefs, toggleApproved,
    aspectRatio, setAspectRatio, tier, setTier,
    selectedShots = [], setSelectedShots,
    withModel, setWithModel,
    selectedMockFiles = [], toggleSelectMockFile, approveManyMockFiles,
    folders = [], onSaveFilesToFolder, folderPicker, setFolderPicker,
    drawings, approvedViews = [],
    busy, onGenerate, onBack, onNext,
  } = props;

  const availableShots = SHOTS_FOR_MODE[mode] || [];
  const toggleShot = (s) => setSelectedShots(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const selectAllShots = () => setSelectedShots(availableShots.slice());
  const clearShots = () => setSelectedShots([]);

  const step1Files = drawings?.files || [];
  const approvedStep1 = step1Files.filter(f => approvedViews.includes(f.view));
  const lockedFrom = approvedStep1.length ? approvedStep1 : step1Files;

  const showModelFields = mode === "lookbook" || mode === "campagna" || (mode === "dettagli" && withModel);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="ai-step-2">
      <div className="card-dark p-6 space-y-4 lg:col-span-2">
        <div className="font-serif text-2xl">Visualizza il capo</div>

        {/* Source-of-truth banner: the Step-1 approved garment is LOCKED */}
        <div
          className={`border p-3 ${lockedFrom.length ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-red-500 bg-red-50"}`}
          data-testid="s2-source-banner"
        >
          <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A] mb-2 inline-flex items-center gap-2">
            <Lock size={11}/>
            {lockedFrom.length
              ? `Using approved garment from Step 1 · ${lockedFrom.length} view${lockedFrom.length > 1 ? "s" : ""} locked`
              : "Nessun capo approvato in Step 1 — torna indietro e genera prima le viste"}
          </div>
          {lockedFrom.length > 0 && (
            <div className="grid grid-cols-4 gap-1">
              {lockedFrom.slice(0, 4).map(f => (
                <div key={f.id} className="aspect-square bg-white border border-[#E5E5E5] overflow-hidden">
                  <img src={fileUrl(f.id)} alt={f.view} className="w-full h-full object-contain"/>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-[#737373] mt-2 leading-relaxed">
            Il design non cambierà. Cambiano solo modello, posa, location, inquadratura, mood, lighting, formato.
          </div>
        </div>

        <FormField label="Modalità">
          <div className="grid grid-cols-2 gap-2" data-testid="s2-modes">
            {VIS_MODES.map(m => (
              <button key={m.key} type="button" onClick={()=>setMode(m.key)}
                className={`text-left p-3 border transition-colors ${mode === m.key ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
                data-testid={`s2-mode-${m.key}`}>
                <div className="text-sm text-[#0A0A0A]">{m.label}</div>
                <div className="text-[10px] text-[#737373]">{m.hint}</div>
              </button>
            ))}
          </div>
        </FormField>

        <FormField label={
          <span className="inline-flex items-center gap-2">
            {mode === "dettagli" ? "Dettagli da generare" : mode === "campagna" ? "Shot campagna" : mode === "no_model" ? "Viste senza modello" : "Shot lookbook"}
            <span className="text-[#737373]">({selectedShots.length})</span>
            <button type="button" onClick={selectAllShots} className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A]" data-testid="s2-shots-all">Tutti</button>
            <button type="button" onClick={clearShots} className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A]" data-testid="s2-shots-none">Nessuno</button>
          </span>
        }>
          <div className="grid grid-cols-3 gap-1.5" data-testid="s2-shots">
            {availableShots.map(s => (
              <button
                key={s} type="button" onClick={()=>toggleShot(s)}
                className={`px-2 py-1.5 text-[11px] border transition-colors ${selectedShots.includes(s) ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0A0A0A]"}`}
                data-testid={`s2-shot-${s}`}
              >{s.replaceAll("_", " ")}</button>
            ))}
          </div>
          {(mode === "lookbook" || mode === "campagna") && selectedShots.length < 6 && (
            <div className="text-[10px] text-[#737373] mt-1">Consigliato ≥ 6 immagini per un set completo.</div>
          )}
        </FormField>

        {mode === "dettagli" && (
          <FormField label="Con modello?">
            <div className="inline-flex gap-2" data-testid="s2-with-model">
              <button type="button" onClick={()=>setWithModel(false)} className={`px-3 py-1.5 text-xs border ${!withModel ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}>Senza modello</button>
              <button type="button" onClick={()=>setWithModel(true)} className={`px-3 py-1.5 text-xs border ${withModel ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}>Con modello</button>
            </div>
          </FormField>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Location">
            <select className="input-dark" value={location} onChange={(e)=>setLocation(e.target.value)} data-testid="s2-location">
              {LOCATION_PRESETS.map(l => <option key={l} value={l}>{l.replaceAll("_", " ")}</option>)}
            </select>
          </FormField>
          <FormField label="Mood">
            <select className="input-dark" value={mood} onChange={(e)=>setMood(e.target.value)} data-testid="s2-mood">
              {MOOD_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </FormField>
        </div>

        {showModelFields && (
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Gender modello">
              <select className="input-dark" value={gender} onChange={(e)=>setGender(e.target.value)}>
                {GENDERS.map(g => <option key={g || "none"} value={g}>{g || "—"}</option>)}
              </select>
            </FormField>
            <FormField label="Body type">
              <select className="input-dark" value={bodyType} onChange={(e)=>setBodyType(e.target.value)}>
                {BODY_TYPES.map(b => <option key={b || "none"} value={b}>{b || "—"}</option>)}
              </select>
            </FormField>
          </div>
        )}

        <FormField label={mode === "campagna" ? "Tono / ambientazione campagna" : "Note aggiuntive"}>
          <textarea rows={2} className="input-dark" value={visNotes} onChange={(e)=>setVisNotes(e.target.value)}
            placeholder={mode === "campagna" ? "Es. Sfondo desertico all'alba, mood cinematografico, energia solitaria..." : "Direzione stilistica, lighting..."}/>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Aspect ratio">
            <select className="input-dark" value={aspectRatio} onChange={(e)=>setAspectRatio(e.target.value)} data-testid="s2-aspect">
              {ASPECT_RATIOS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </FormField>
          <div/>
        </div>

        <TierPicker tier={tier} setTier={setTier} testid="s2-tier"/>

        <button
          disabled={busy || lockedFrom.length === 0 || selectedShots.length === 0}
          onClick={onGenerate}
          className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="s2-generate"
          title={lockedFrom.length === 0 ? "Prima approva almeno una vista nello Step 1" : selectedShots.length === 0 ? "Seleziona almeno uno shot" : ""}
        >
          {busy ? <><Loader2 size={14} className="animate-spin"/> Genero set ({selectedShots.length})…</> : <><Sparkles size={14}/> Genera set · {selectedShots.length} immagini</>}
        </button>

        <div className="flex justify-between pt-3 border-t border-[#E5E5E5]">
          <button onClick={onBack} className="btn-ghost text-xs inline-flex items-center gap-2"><ChevronLeft size={12}/> Torna a Step 1</button>
          <button onClick={onNext} disabled={mockups.length === 0} className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-40" data-testid="s2-next">
            Vai al Tech Pack <ChevronRight size={12}/>
          </button>
        </div>
      </div>

      {/* Results gallery */}
      <div className="lg:col-span-3 space-y-4">
        {mockups.length === 0 && (
          <div className="card-dark p-10 text-center text-[#737373]">
            <ImageIcon size={40} className="mx-auto text-[#D4D4D4] mb-4"/>
            <div className="font-serif text-xl text-[#0A0A0A] mb-2">Galleria visualizzazioni</div>
            <div className="text-xs uppercase tracking-widest">Ogni set produce più immagini · seleziona · approva · salva in cartella.</div>
          </div>
        )}

        {mockups.length > 0 && (
          <div className="card-dark p-3 flex flex-wrap items-center gap-2 justify-between" data-testid="s2-bulk-bar">
            <div className="text-[11px] uppercase tracking-widest text-[#525252]">
              Selezionate: {selectedMockFiles.length} · Approvate: {approvedRefs.length}
            </div>
            <div className="inline-flex flex-wrap items-center gap-2">
              <button
                disabled={!selectedMockFiles.length}
                onClick={()=>approveManyMockFiles(selectedMockFiles)}
                className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="s2-approve-selected"
              >
                <Check size={12}/> Approva selezione
              </button>
              <button
                disabled={!selectedMockFiles.length}
                onClick={()=>setFolderPicker({ fileIds: selectedMockFiles.slice() })}
                className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="s2-save-selection"
              >
                <FolderPlus size={12}/> Salva selezione in cartella
              </button>
            </div>
          </div>
        )}

        {mockups.map(entry => {
          const okFiles = entry.files || [];
          const okIds = okFiles.map(f => f.id);
          const allSelected = okIds.length > 0 && okIds.every(id => selectedMockFiles.includes(id));
          return (
            <div key={entry.id} className="card-dark p-4" data-testid={`s2-batch-${entry.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="label-caps text-[#0A0A0A]">
                  {VIS_MODES.find(m => m.key === entry.mode)?.label}
                  {entry.shots?.length ? ` · ${entry.shots.length} shot` : ""}
                  {entry.location ? ` · ${entry.location.replaceAll("_"," ")}` : ""}
                </div>
                <div className="inline-flex items-center gap-2">
                  <button
                    onClick={()=>{
                      if (allSelected) {
                        // deselect all in batch
                        okIds.forEach(id => selectedMockFiles.includes(id) && toggleSelectMockFile(id));
                      } else {
                        okIds.forEach(id => !selectedMockFiles.includes(id) && toggleSelectMockFile(id));
                      }
                    }}
                    className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A]"
                    data-testid={`s2-batch-select-all-${entry.id}`}
                  >
                    {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                  <button
                    onClick={()=>setFolderPicker({ fileIds: okIds.slice() })}
                    disabled={!okIds.length}
                    className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid={`s2-batch-save-all-${entry.id}`}
                    title="Salva l'intero pacchetto in cartella"
                  >
                    <FolderPlus size={11}/> Salva pacchetto
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {okFiles.map(f => {
                  const isApproved = approvedRefs.includes(f.id);
                  const isSelected = selectedMockFiles.includes(f.id);
                  return (
                    <div key={f.id} className={`relative border overflow-hidden ${isApproved ? "border-[#0A0A0A] ring-2 ring-inset ring-[#0A0A0A]/20" : isSelected ? "border-[#0A0A0A]" : "border-[#E5E5E5]"}`}>
                      <img
                        src={fileUrl(f.id)} alt={f.original_filename}
                        className="w-full h-48 object-cover"
                        data-testid={`s2-img-${f.id}`}
                        onError={(ev)=>{ ev.currentTarget.style.display = "none"; }}
                      />
                      {f.shot && <div className="absolute top-1 left-1 text-[9px] uppercase tracking-widest bg-white/90 text-[#0A0A0A] px-1.5 py-0.5">{f.shot.replaceAll("_"," ")}</div>}
                      <button
                        onClick={()=>toggleSelectMockFile(f.id)}
                        className={`absolute top-1 right-1 w-6 h-6 inline-flex items-center justify-center text-[10px] uppercase tracking-widest border ${isSelected ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white/90 text-[#0A0A0A] border-[#0A0A0A]"}`}
                        data-testid={`s2-select-${f.id}`}
                        title={isSelected ? "Deseleziona" : "Seleziona"}
                      >
                        {isSelected ? <Check size={11}/> : ""}
                      </button>
                      <button
                        onClick={()=>toggleApproved(f.id)}
                        className={`absolute bottom-1 left-1 right-1 text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1 ${isApproved ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] border border-[#0A0A0A]"}`}
                        data-testid={`s2-approve-${f.id}`}
                      >
                        {isApproved ? <><Check size={10}/> Approvato</> : "Approva"}
                      </button>
                    </div>
                  );
                })}
                {/* Show failed shots explicitly */}
                {(entry.status || []).filter(st => st.status !== "ok").map((st, i) => (
                  <div key={`err-${i}`} className="border border-[#E5E5E5] p-3 text-[10px] uppercase tracking-widest text-red-600 flex items-center justify-center min-h-[192px]">
                    {st.shot ? `${st.shot.replaceAll("_"," ")} · ` : ""}fallita
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {folderPicker && (
        <FolderPickerModal
          folders={folders}
          count={folderPicker.fileIds?.length || 0}
          onClose={()=>setFolderPicker(null)}
          onPick={(folder)=>onSaveFilesToFolder(folderPicker.fileIds, folder)}
        />
      )}
    </div>
  );
}

// ==================== STEP 3 ====================
function Step3(props) {
  const {
    techpack, setTechpack, drawings, mockups, approvedRefs,
    garments, garmentId, setGarmentId, busy,
    onGenerate, onSave, onSaveToCollection, onExportPdf,
    tpOpen, setTpOpen, onBack,
  } = props;
  const patch = (path, v) => {
    setTechpack(prev => {
      const next = { ...(prev || {}) };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = v;
      return next;
    });
  };
  const approvedFiles = mockups.flatMap(m => m.files || []).filter(f => approvedRefs.includes(f.id));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="ai-step-3">
      <div className="card-dark p-6 space-y-4 lg:col-span-1">
        <div className="font-serif text-2xl">Tech Pack</div>

        <FormField label="Collega a un capo esistente (opzionale)">
          <select className="input-dark" value={garmentId} onChange={(e)=>setGarmentId(e.target.value)} data-testid="s3-garment">
            <option value="">— Nessuno —</option>
            {garments.map(g => <option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}
          </select>
        </FormField>

        <button onClick={onGenerate} disabled={busy} className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="s3-generate">
          {busy ? <><Loader2 size={14} className="animate-spin"/> Generazione…</> : <><Sparkles size={14}/> {techpack ? "Rigenera bozza" : "Genera bozza tech pack"}</>}
        </button>

        {techpack && (
          <>
            <button onClick={onSave} disabled={busy} className="btn-ghost w-full inline-flex items-center justify-center gap-2" data-testid="s3-save">
              <Save size={12}/> Salva bozza {garmentId ? "sul capo" : "localmente"}
            </button>
            <button onClick={onSaveToCollection} disabled={busy} className="btn-gold w-full inline-flex items-center justify-center gap-2" data-testid="s3-save-collection">
              <BookmarkPlus size={12}/> {garmentId ? "Aggiorna in Collezioni & Capi" : "Salva in Collezioni & Capi"}
            </button>
            <button onClick={onExportPdf} disabled={busy} className="btn-ghost w-full inline-flex items-center justify-center gap-2" data-testid="s3-export-pdf">
              <FileDown size={12}/> Export Tech Pack PDF
            </button>
          </>
        )}

        <div className="flex justify-between pt-3 border-t border-[#E5E5E5]">
          <button onClick={onBack} className="btn-ghost text-xs inline-flex items-center gap-2"><ChevronLeft size={12}/> Indietro</button>
        </div>

        <div className="text-[10px] text-[#737373] pt-2 border-t border-[#E5E5E5]">
          Le viste tecniche e le reference approvate sono già collegate al tech pack qui accanto.
        </div>
      </div>

      <div className="lg:col-span-2 space-y-3">
        {!techpack && !busy && (
          <div className="card-dark p-10 text-center text-[#737373]" data-testid="s3-empty">
            <FileText size={40} className="mx-auto text-[#D4D4D4] mb-4"/>
            <div className="font-serif text-xl text-[#0A0A0A] mb-2">Nessuna bozza ancora</div>
            <div className="text-xs uppercase tracking-widest">Clicca "Genera bozza" a sinistra per creare il tech pack.</div>
          </div>
        )}

        {techpack && (
          <>
            <Section title="General info" open={tpOpen.general} onToggle={()=>setTpOpen({...tpOpen, general: !tpOpen.general})} testid="s3-general">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Nome capo"><input className="input-dark" value={techpack.general?.name || ""} onChange={(e)=>patch("general.name", e.target.value)}/></FormField>
                <FormField label="Codice"><input className="input-dark" value={techpack.general?.code || ""} onChange={(e)=>patch("general.code", e.target.value)}/></FormField>
                <FormField label="Categoria"><input className="input-dark" value={techpack.general?.category || ""} onChange={(e)=>patch("general.category", e.target.value)}/></FormField>
                <FormField label="Stagione"><input className="input-dark" value={techpack.general?.season || ""} onChange={(e)=>patch("general.season", e.target.value)}/></FormField>
                <FormField label="Gender / target"><input className="input-dark" value={techpack.general?.gender || ""} onChange={(e)=>patch("general.gender", e.target.value)}/></FormField>
              </div>
              <FormField label="Descrizione prodotto">
                <textarea rows={3} className="input-dark" value={techpack.general?.description || ""} onChange={(e)=>patch("general.description", e.target.value)}/>
              </FormField>
            </Section>

            <Section title="Technical views" open={tpOpen.views} onToggle={()=>setTpOpen({...tpOpen, views: !tpOpen.views})} testid="s3-views">
              {drawings?.files?.length > 0 ? (
                <div className="grid grid-cols-4 gap-3">
                  {drawings.files.map(f => (
                    <div key={f.id} className="border border-[#E5E5E5]">
                      <img src={fileUrl(f.id)} alt={f.view} className="w-full aspect-square object-contain bg-white"/>
                      <div className="p-2 text-[10px] uppercase tracking-widest text-center border-t border-[#E5E5E5]">{VIEW_LABELS[f.view] || f.view}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-[#737373] italic">Nessuna vista tecnica. Torna allo Step 1 per generarle.</div>}
            </Section>

            <Section title="Reference images" open={tpOpen.refs} onToggle={()=>setTpOpen({...tpOpen, refs: !tpOpen.refs})} testid="s3-refs">
              {approvedFiles.length > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {approvedFiles.map(f => (
                    <img key={f.id} src={fileUrl(f.id)} alt="ref" className="w-full h-32 object-cover border border-[#E5E5E5]"/>
                  ))}
                </div>
              ) : <div className="text-xs text-[#737373] italic">Nessuna reference approvata. Torna allo Step 2 per selezionarne.</div>}
            </Section>

            <Section title="Materials" open={tpOpen.materials} onToggle={()=>setTpOpen({...tpOpen, materials: !tpOpen.materials})} testid="s3-materials">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Main fabric"><input className="input-dark" value={techpack.materials?.main_fabric || ""} onChange={(e)=>patch("materials.main_fabric", e.target.value)}/></FormField>
                <FormField label="Secondary fabrics"><input className="input-dark" value={techpack.materials?.secondary_fabrics || ""} onChange={(e)=>patch("materials.secondary_fabrics", e.target.value)}/></FormField>
                <FormField label="Composition"><input className="input-dark" value={techpack.materials?.composition || ""} onChange={(e)=>patch("materials.composition", e.target.value)}/></FormField>
                <FormField label="Weight (gsm)"><input className="input-dark" value={techpack.materials?.weight || ""} onChange={(e)=>patch("materials.weight", e.target.value)}/></FormField>
                <FormField label="Color"><input className="input-dark" value={techpack.materials?.color || ""} onChange={(e)=>patch("materials.color", e.target.value)}/></FormField>
                <FormField label="Supplier"><input className="input-dark" value={techpack.materials?.supplier || ""} onChange={(e)=>patch("materials.supplier", e.target.value)}/></FormField>
                <FormField label="Supplier code"><input className="input-dark" value={techpack.materials?.supplier_code || ""} onChange={(e)=>patch("materials.supplier_code", e.target.value)}/></FormField>
                <FormField label="Consumption"><input className="input-dark" value={techpack.materials?.quantity || ""} onChange={(e)=>patch("materials.quantity", e.target.value)}/></FormField>
              </div>
              <FormField label="Notes"><textarea rows={2} className="input-dark" value={techpack.materials?.notes || ""} onChange={(e)=>patch("materials.notes", e.target.value)}/></FormField>
            </Section>

            <Section title="Trims & components" open={tpOpen.trims} onToggle={()=>setTpOpen({...tpOpen, trims: !tpOpen.trims})} testid="s3-trims">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Buttons"><input className="input-dark" value={techpack.trims?.buttons || ""} onChange={(e)=>patch("trims.buttons", e.target.value)}/></FormField>
                <FormField label="Zippers"><input className="input-dark" value={techpack.trims?.zippers || ""} onChange={(e)=>patch("trims.zippers", e.target.value)}/></FormField>
                <FormField label="Snaps"><input className="input-dark" value={techpack.trims?.snaps || ""} onChange={(e)=>patch("trims.snaps", e.target.value)}/></FormField>
                <FormField label="Drawcords"><input className="input-dark" value={techpack.trims?.drawcords || ""} onChange={(e)=>patch("trims.drawcords", e.target.value)}/></FormField>
                <FormField label="Elastic"><input className="input-dark" value={techpack.trims?.elastic || ""} onChange={(e)=>patch("trims.elastic", e.target.value)}/></FormField>
                <FormField label="Labels"><input className="input-dark" value={techpack.trims?.labels || ""} onChange={(e)=>patch("trims.labels", e.target.value)}/></FormField>
                <FormField label="Hangtags"><input className="input-dark" value={techpack.trims?.hangtags || ""} onChange={(e)=>patch("trims.hangtags", e.target.value)}/></FormField>
                <FormField label="Packaging"><input className="input-dark" value={techpack.trims?.packaging || ""} onChange={(e)=>patch("trims.packaging", e.target.value)}/></FormField>
                <FormField label="Other accessories"><input className="input-dark" value={techpack.trims?.accessories || ""} onChange={(e)=>patch("trims.accessories", e.target.value)}/></FormField>
              </div>
            </Section>

            <Section title="Graphics / workmanship" open={!!tpOpen.graphics} onToggle={()=>setTpOpen({...tpOpen, graphics: !tpOpen.graphics})} testid="s3-graphics">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Prints"><input className="input-dark" value={techpack.graphics?.prints || ""} onChange={(e)=>patch("graphics.prints", e.target.value)}/></FormField>
                <FormField label="Embroidery"><input className="input-dark" value={techpack.graphics?.embroidery || ""} onChange={(e)=>patch("graphics.embroidery", e.target.value)}/></FormField>
                <FormField label="Applications"><input className="input-dark" value={techpack.graphics?.applications || ""} onChange={(e)=>patch("graphics.applications", e.target.value)}/></FormField>
                <FormField label="Washing"><input className="input-dark" value={techpack.graphics?.washing || ""} onChange={(e)=>patch("graphics.washing", e.target.value)}/></FormField>
                <FormField label="Treatments"><input className="input-dark" value={techpack.graphics?.treatments || ""} onChange={(e)=>patch("graphics.treatments", e.target.value)}/></FormField>
                <FormField label="Special workmanship"><input className="input-dark" value={techpack.graphics?.special || ""} onChange={(e)=>patch("graphics.special", e.target.value)}/></FormField>
              </div>
            </Section>

            <Section title="Construction details" open={tpOpen.construction} onToggle={()=>setTpOpen({...tpOpen, construction: !tpOpen.construction})} testid="s3-construction">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Seams"><input className="input-dark" value={techpack.construction?.seams || ""} onChange={(e)=>patch("construction.seams", e.target.value)}/></FormField>
                <FormField label="Stitching"><input className="input-dark" value={techpack.construction?.stitching || ""} onChange={(e)=>patch("construction.stitching", e.target.value)}/></FormField>
                <FormField label="Pockets"><input className="input-dark" value={techpack.construction?.pockets || ""} onChange={(e)=>patch("construction.pockets", e.target.value)}/></FormField>
                <FormField label="Closures"><input className="input-dark" value={techpack.construction?.closures || ""} onChange={(e)=>patch("construction.closures", e.target.value)}/></FormField>
                <FormField label="Finishing"><input className="input-dark" value={techpack.construction?.finishing || ""} onChange={(e)=>patch("construction.finishing", e.target.value)}/></FormField>
                <FormField label="Internal construction"><input className="input-dark" value={techpack.construction?.internal || ""} onChange={(e)=>patch("construction.internal", e.target.value)}/></FormField>
              </div>
              <FormField label="Assembly notes"><textarea rows={3} className="input-dark" value={techpack.construction?.assembly || ""} onChange={(e)=>patch("construction.assembly", e.target.value)}/></FormField>
            </Section>

            <Section title="Measurements" open={tpOpen.measurements} onToggle={()=>setTpOpen({...tpOpen, measurements: !tpOpen.measurements})} testid="s3-measurements">
              <FormField label="Sample size"><input className="input-dark" value={techpack.measurements?.sample_size || ""} onChange={(e)=>patch("measurements.sample_size", e.target.value)}/></FormField>
              <FormField label="Points of measure">
                <textarea rows={4} className="input-dark font-mono" value={techpack.measurements?.points || ""} onChange={(e)=>patch("measurements.points", e.target.value)} placeholder="Chest / Waist / Hip / Length / Sleeve..."/>
              </FormField>
              <FormField label="Grading / size chart">
                <textarea rows={4} className="input-dark font-mono" value={techpack.measurements?.grading || ""} onChange={(e)=>patch("measurements.grading", e.target.value)} placeholder="XS · S · M · L · XL"/>
              </FormField>
            </Section>

            <Section title="Supplier notes" open={tpOpen.supplier} onToggle={()=>setTpOpen({...tpOpen, supplier: !tpOpen.supplier})} testid="s3-supplier">
              <FormField label="Note per il fornitore">
                <textarea rows={5} className="input-dark" value={techpack.supplier_notes || ""} onChange={(e)=>patch("supplier_notes", e.target.value)}/>
              </FormField>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

// -------------------- Small helpers --------------------
function FormField({ label, children }) {
  return (
    <div>
      <label className="label-caps block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, open, onToggle, children, testid }) {
  return (
    <div className="card-dark" data-testid={testid}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
        <div className="font-serif text-lg text-[#0A0A0A]">{title}</div>
        {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function TierPicker({ tier, setTier, testid }) {
  return (
    <div data-testid={testid}>
      <label className="label-caps block mb-2">Qualità</label>
      <div className="grid grid-cols-3 gap-2">
        {TIERS.map(t => (
          <button
            key={t.key} type="button" onClick={() => setTier(t.key)}
            className={`text-left p-2 border transition-colors ${tier === t.key ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            data-testid={`${testid}-${t.key}`}
          >
            <div className="text-xs uppercase tracking-widest text-[#0A0A0A]">{t.label}</div>
            <div className="text-[10px] text-[#737373]">{t.hint}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StylePicker({ style, setStyle, testid }) {
  return (
    <div data-testid={testid}>
      <label className="label-caps block mb-2">Stile output</label>
      <div className="grid grid-cols-3 gap-2">
        {STYLES.map(s => (
          <button
            key={s.key} type="button" onClick={() => setStyle(s.key)}
            className={`text-left p-2 border transition-colors ${style === s.key ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            data-testid={`${testid}-${s.key}`}
          >
            <div className="text-xs uppercase tracking-widest text-[#0A0A0A]">{s.label}</div>
            <div className="text-[10px] text-[#737373]">{s.hint}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FolderPickerModal({ folders, count, onClose, onPick }) {
  const [tab, setTab] = useState("existing");
  const [pickId, setPickId] = useState(folders[0]?.id || "");
  const [newName, setNewName] = useState("AI Studio");
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="folder-picker">
      <div className="bg-white w-[420px] max-w-[92vw] border border-[#0A0A0A] shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
          <div className="text-xs uppercase tracking-widest">Salva {count} immagin{count === 1 ? "e" : "i"} in cartella</div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="folder-picker-close"><X size={14}/></button>
        </div>
        <div className="flex border-b border-[#E5E5E5]">
          <button onClick={()=>setTab("existing")} className={`flex-1 py-2 text-[11px] uppercase tracking-widest ${tab === "existing" ? "bg-[#0A0A0A] text-white" : "text-[#525252]"}`} data-testid="folder-picker-existing-tab">Cartella esistente</button>
          <button onClick={()=>setTab("new")} className={`flex-1 py-2 text-[11px] uppercase tracking-widest ${tab === "new" ? "bg-[#0A0A0A] text-white" : "text-[#525252]"}`} data-testid="folder-picker-new-tab">Nuova cartella</button>
        </div>
        <div className="p-4 space-y-3">
          {tab === "existing" && (
            <>
              <label className="label-caps block">Cartella</label>
              <select className="input-dark" value={pickId} onChange={(e)=>setPickId(e.target.value)} data-testid="folder-picker-select">
                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <button
                onClick={()=>onPick({ id: pickId })}
                disabled={!pickId}
                className="btn-gold w-full text-xs disabled:opacity-40"
                data-testid="folder-picker-confirm-existing"
              >Salva qui</button>
            </>
          )}
          {tab === "new" && (
            <>
              <label className="label-caps block">Nome nuova cartella</label>
              <input className="input-dark" value={newName} onChange={(e)=>setNewName(e.target.value)} data-testid="folder-picker-new-name"/>
              <button
                onClick={()=>onPick({ name: newName.trim() || "AI Studio" })}
                className="btn-gold w-full text-xs"
                data-testid="folder-picker-confirm-new"
              >Crea e salva</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
